import { action, makeObservable, observable, toJS } from "mobx"
import { ServiceReview, ServiceReviewUpdate } from "../../../domain/entities/ServiceReview"
import { GetServiceReviewsUseCase } from "../../../domain/useCases/serviceReviews/getServiceReviews"
import { UpdateServiceReviewUseCase } from "../../../domain/useCases/serviceReviews/updateServiceReview"
import { DeleteServiceReviewUseCase } from "../../../domain/useCases/serviceReviews/deleteServiceReview"
import {
	CreateServiceReviewUseCase,
	CreateServiceReviewParams
} from "../../../domain/useCases/serviceReviews/createServiceReview"
import { SearchUsersUseCase } from "../../../domain/useCases/users/searchUsers"
import { User } from "../../../domain/entities/User"
import { ErrorHandler } from "../../error/ErrorHandler"
import { TableConfig } from "../../components/DataTable/types/TableConfig"
import { AuthStore } from "../../stores/AuthStore"
import config from "../../../config/config"

interface Props {
	GetServiceReviewsUseCase: GetServiceReviewsUseCase
	CreateServiceReviewUseCase: CreateServiceReviewUseCase
	UpdateServiceReviewUseCase: UpdateServiceReviewUseCase
	DeleteServiceReviewUseCase: DeleteServiceReviewUseCase
	SearchUsersUseCase: SearchUsersUseCase
	ErrorHandler: ErrorHandler
	AuthStore: AuthStore
}

export class ServiceReviewsViewModel {
	private _getServiceReviewsUseCase: GetServiceReviewsUseCase
	private _createServiceReviewUseCase: CreateServiceReviewUseCase
	private _updateServiceReviewUseCase: UpdateServiceReviewUseCase
	private _deleteServiceReviewUseCase: DeleteServiceReviewUseCase
	private _searchUsersUseCase: SearchUsersUseCase
	private _errorHandler: ErrorHandler
	private _authStore: AuthStore

	isLoading: boolean = false
	serviceReviews: ServiceReview[] = []
	serviceId: number = 0
	serviceName: string = ""
	editingReviewId: number | null = null
	searchedUsers: Partial<User>[] = []
	isLoadingUsers: boolean = false
	tableConfig: TableConfig = {
		pageSize: config.ui.componentsConfig.dataTablePageSize || 20,
		sort: { order: "descend", field: "date" }
	}

	constructor({
		GetServiceReviewsUseCase,
		CreateServiceReviewUseCase,
		UpdateServiceReviewUseCase,
		DeleteServiceReviewUseCase,
		SearchUsersUseCase,
		ErrorHandler,
		AuthStore
	}: Props) {
		makeObservable(this, {
			isLoading: observable,
			serviceReviews: observable,
			serviceId: observable,
			serviceName: observable,
			editingReviewId: observable,
			searchedUsers: observable,
			isLoadingUsers: observable,
			tableConfig: observable,
			setLoading: action,
			setServiceReviews: action,
			setServiceId: action,
			setServiceName: action,
			setEditingReviewId: action,
			setSearchedUsers: action,
			setIsLoadingUsers: action,
			setTableConfig: action,
			updateReviewInList: action
		})

		this._getServiceReviewsUseCase = GetServiceReviewsUseCase
		this._createServiceReviewUseCase = CreateServiceReviewUseCase
		this._updateServiceReviewUseCase = UpdateServiceReviewUseCase
		this._deleteServiceReviewUseCase = DeleteServiceReviewUseCase
		this._searchUsersUseCase = SearchUsersUseCase
		this._errorHandler = ErrorHandler
		this._authStore = AuthStore
	}

	public async fetchServiceReviews(serviceId: number, serviceName?: string) {
		try {
			this.setLoading(true)
			this.setServiceId(serviceId)
			if (serviceName) this.setServiceName(serviceName)

			const reviews = await this._getServiceReviewsUseCase.exec({ serviceId })
			this.setServiceReviews(reviews)
		} catch (error: any) {
			console.error("Error fetching reviews:", error)
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	public async createReview(params: CreateServiceReviewParams) {
		const tempReview: ServiceReview = {
			id: Date.now(),
			user_id: params.userId,
			service_id: params.serviceId,
			review: params.review,
			rating: params.rating,
			user: {
				id: params.userId,
				name: params.userName,
				email: params.userEmail
			},
			date: new Date()
		}

		try {
			this.setServiceReviews([tempReview, ...this.serviceReviews])

			this.setLoading(true)

			const response = await this._createServiceReviewUseCase.exec(params)

			if (response && response.id) {
				const updatedReviews = this.serviceReviews.map(review =>
					review.id === tempReview.id
						? {
								...response,
								user: response.user || tempReview.user
						  }
						: review
				)
				this.setServiceReviews(updatedReviews)
			}

			return response
		} catch (error: any) {
			console.error("Error creating review:", error)

			const reviewsWithoutTemp = this.serviceReviews.filter(review => review.id !== tempReview.id)
			this.setServiceReviews(reviewsWithoutTemp)

			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	public async updateReview(reviewUpdate: ServiceReviewUpdate, userInfo?: { name?: string; email?: string }) {
		try {
			this.setLoading(true)
			await this._updateServiceReviewUseCase.exec({ reviewUpdate })

			this.updateReviewInList(reviewUpdate, userInfo)
			this.setEditingReviewId(null)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	public async deleteReview(reviewId: number) {
		try {
			this.setLoading(true)

			const isTemporaryReview = reviewId > 1000000000000

			if (isTemporaryReview) {
				const updatedReviews = this.serviceReviews.filter(review => review.id !== reviewId)
				this.setServiceReviews(updatedReviews)
			} else {
				await this._deleteServiceReviewUseCase.exec({ reviewId })

				const updatedReviews = this.serviceReviews.filter(review => review.id !== reviewId)
				this.setServiceReviews(updatedReviews)
			}
		} catch (error: any) {
			console.error("Error deleting review:", error)
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	public getRatingStars(rating?: number): string {
		if (!rating) return "Sin calificación"
		return "★".repeat(rating) + "☆".repeat(5 - rating)
	}

	public formatDate(date: Date): string {
		return new Date(date).toLocaleDateString("es-ES", {
			year: "numeric",
			month: "long",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit"
		})
	}

	public startEditingReview(reviewId: number) {
		this.setEditingReviewId(reviewId)
	}

	public cancelEditing() {
		this.setEditingReviewId(null)
	}

	public isEditingReview(reviewId: number): boolean {
		return this.editingReviewId === reviewId
	}

	setLoading(isLoading: boolean) {
		this.isLoading = isLoading
	}

	setServiceReviews(reviews: ServiceReview[]) {
		this.serviceReviews = reviews
	}

	setServiceId(serviceId: number) {
		this.serviceId = serviceId
	}

	setServiceName(serviceName: string) {
		this.serviceName = serviceName
	}

	setEditingReviewId(reviewId: number | null) {
		this.editingReviewId = reviewId
	}

	setTableConfig(config: Partial<typeof this.tableConfig>) {
		this.tableConfig = { ...this.tableConfig, ...config }
	}

	updateReviewInList(reviewUpdate: ServiceReviewUpdate, userInfo?: { name?: string; email?: string }) {
		const updatedReviews = this.serviceReviews.map(review =>
			review.id === reviewUpdate.id
				? {
						...review,
						...reviewUpdate,
						user_id: reviewUpdate.userId,
						user: userInfo
							? {
									id: reviewUpdate.userId,
									name: userInfo.name,
									email: userInfo.email
							  }
							: review.user
				  }
				: review
		)
		this.setServiceReviews(updatedReviews)
	}

	public async searchUsers(expression: string) {
		if (!expression.trim()) {
			this.setSearchedUsers([])
			return
		}
		try {
			this.setIsLoadingUsers(true)
			const users = await this._searchUsersUseCase.exec({
				expression: expression.trim(),
				limit: 10
			})
			this.setSearchedUsers(users)
		} catch (error: any) {
			console.error("Error searching users:", error)
			this.setSearchedUsers([])
		} finally {
			this.setIsLoadingUsers(false)
		}
	}

	setSearchedUsers(users: Partial<User>[]) {
		this.searchedUsers = users
	}

	setIsLoadingUsers(isLoading: boolean) {
		this.isLoadingUsers = isLoading
	}
}
